<script setup lang="ts">
import { onMounted, ref, onUnmounted } from 'vue'
import { toast } from 'vue-sonner'
import { AreaChart, Search, RefreshCw, PlusIcon } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import {
  Dialog
} from '@/components/ui/dialog'
import aplosApi from '@/services/aplosApi'
import { toTitleCase, toShortString } from '@/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { type Tenant } from '@/types'
import { useRouter } from 'vue-router'
import { useTenantStore } from '@/store/tenantStore'
import { Button } from '@/components/ui/button';
import Select from '../ui/select/Select.vue'
import SelectTrigger from '../ui/select/SelectTrigger.vue'
import SelectValue from '../ui/select/SelectValue.vue'
import SelectContent from '../ui/select/SelectContent.vue'
import SelectGroup from '../ui/select/SelectGroup.vue'
import SelectItem from '../ui/select/SelectItem.vue'
import { useUser } from '@/composables/useUser'
import Loading from '@/components/Loading.vue'
import { searchColorMap } from '@/utils/index'
import SubscriptionStatusCell from '@/components/ManageTenants/SubscriptionStatusCell.vue'
import AppMenuBar from "@/components/Menu/AppMenuBar.vue";
const auth = useUser()




type Props = {
  displayLimit?: number
  search?: string
}

const columns = ref([
  {
    title: 'Tenant ID',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Tenant Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a: Tenant, b: Tenant): number => { return a.name.toLowerCase().localeCompare(b.name.toLowerCase()); },
  },
  {
    title: 'Tenant Status',
    dataIndex: 'status',
    key: 'status',
    filters: [],
    onFilter: (value: string, record: any) => record.status?.toLowerCase().startsWith(value),
  },
  {
    title: 'Subscription Status',
    key: 'subscriptionStatus',
  },
  {
    title: 'Plan Tier',
    dataIndex: 'planTier',
    key: 'planTier',
    filters: [],
    onFilter: (value: string, record: any) => record.planTier?.toLowerCase().startsWith(value),
  },
  {
    title: 'Max Users',
    dataIndex: 'maxUsers',
    key: 'maxUsers',
  },
]);
const props = defineProps<Props>()
const { tenantData, tenants: storedTenants } = useTenantStore();


const tenants = ref<Tenant[]>([])
const originalTenants = ref<Tenant[]>([]);
const loading = ref(true)
const router = useRouter();
const filterOptions = ["all", "active", "inactive", "archived"];
const selectedFilter = ref(filterOptions[1]);

function generateFilters(data:any, field:any) {
  const uniqueValues = [...new Set(data.map((item:any) => item[field]?.toLowerCase()))].filter(Boolean);
  return uniqueValues.map((value:any) => ({
    text: toTitleCase(value),
    value,
  }));
}



function load_tenant_screen(tenant_id: string | null | undefined) {

  if (!tenant_id){
    // todo toast message
    console.error(`bad tenant id: ${tenant_id}`)
    return
  }
  const path = `/app/administration/tenant-management/edit/${tenant_id}`

  router.push({
    path: path
  });


}

async function loadData() {
  loading.value = true
  try {

    await auth.initialize()

    const global_admin = auth.is_global_admin()

    if (!global_admin){
      // push them to a different route
      load_tenant_screen(auth.user.tenant_id)
      return
    }

    const tenant_id =  global_admin ? "" : auth.user.tenant_id;
    const { data } = await aplosApi.getTenants(tenant_id,selectedFilter.value)

    let items: Tenant[];
    if (Array.isArray(data)) {
      items = data;
    } else {
      throw new Error("Response is not a valid array.");
    }
    items.sort((a: Tenant, b: Tenant) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    (tenantData as any).data = items;
    columns.value = columns.value.map((column: any) => {
      if (['status', 'planTier'].includes(column.key)) {
        return {
          ...column,
          filters: generateFilters(items, column.key),
        };
      }
      return column;
    });
    storedTenants.splice(0, storedTenants.length, ...items);

    originalTenants.value = items;
    tenants.value = props.displayLimit ? items.slice(0, props.displayLimit) : items;
  } catch (error) {
    tenants.value = []
    toast.error('Failed to load Tenants')
  }finally{
    loading.value = false
  }
}

function handleSearch(value: string) {
  let searchKeyword = value?.toLowerCase();
  loading.value = true
  if (searchKeyword.length > 0) {
    const filterRecords = originalTenants.value.filter(tenant => {
      const fieldsToCheck = [
        tenant.id?.toLowerCase() ?? '',
        tenant.name?.toLowerCase() ?? '',
        tenant.status?.toLowerCase() ?? '',
        tenant.planTier?.toLowerCase() ?? '',
      ];
      return fieldsToCheck.some(field => field.includes(searchKeyword));
    });
    tenants.value = props.displayLimit ? filterRecords.slice(0, props.displayLimit) : filterRecords;
  } else {
    tenants.value = props.displayLimit ? originalTenants.value.slice(0, props.displayLimit) : originalTenants.value;
  }
  loading.value = false
}
const handleNameClick = (tenant: any) => {
  load_tenant_screen(tenant.id)
};

onMounted(() => {
  loadData();

})
onUnmounted(() => {

})

async function handleRefresh() {

  await loadData();
}

const navButtons = ref([
  {
    icon: RefreshCw,
    onClick: handleRefresh,
    animate: loading,
    title: "",
    toolTip: "Refresh",
  },
  {
    icon: PlusIcon,
    title: "",
    toolTip: "Add Tenant",
    href: "/app/administration/tenant-management/new"
  },
]);
</script>

<template>
  <Loading v-if="loading || !auth.is_global_admin()"></Loading>
  <div v-else>
    <AppMenuBar :showSearch="true" :onSearch="handleSearch" :buttons="navButtons" />
    <div class="w-full">
      <div class="flex justify-end">
        <div class="w-[20%] flex gap-4">
          <p class="flex items-center">Results:</p>
          <Select v-model="selectedFilter">
            <SelectTrigger class="w-[100%]">
              <SelectValue :placeholder="selectedFilter" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem v-for="option in filterOptions" :key="option" :value="option">
                  {{ toTitleCase(option) }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Button variant="outline" @click="loadData">
            <Search class="size-4" />
          </Button>
        </div>
      </div>
    </div>
    <div class="mb-2"></div>

    <Dialog>
      <a-table
        :dataSource="tenants"
        :columns="columns"
        :pagination="!!!props.displayLimit && { showSizeChanger: tenants.length >= 20 }"
        class="overflow-x-auto"
        rowKey="id"
        :customRow="(record: any) => ({
        onClick: !loading ? () => handleNameClick(record) : null,
        class: !loading ? 'cursor-pointer' : ''
      })"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="loading">
            <Skeleton class="my-4 h-6 w-full" />
          </template>
          <template v-else-if="column.key === 'id'">
            <a-tooltip>
              <template #title>Tenant ID:{{ record.id }}</template>
              {{ toShortString(record.id, 10) }}
            </a-tooltip>
          </template>
          <template v-else-if="column.key === 'status'">
            <span role="status">
              <Badge :class="searchColorMap(record.status)">
                {{ toTitleCase(record?.status ?? "NA") }}
              </Badge>
            </span>
          </template>
          <template v-else-if="column.key === 'subscriptionStatus'">
            <SubscriptionStatusCell :tenantId="record.id" />
          </template>
          <template v-else-if="column.key === 'planTier'">
            <span>
              {{ toTitleCase(record.planTier ?? "N/A") }}
            </span>
          </template>
          <template v-else-if="column.key === 'maxUsers'">
            <span>
              {{ record.maxUsers != null ? record.maxUsers : "Unlimited" }}
            </span>
          </template>
        </template>

        <template #emptyText>
          <div class="py-10">
            <AreaChart class="mx-auto size-14 text-muted-foreground" />
            <p class="my-5">Manage Tenants</p>
          </div>
        </template>
      </a-table>
    </Dialog>

    <a id="file-download" download class="sr-only" />
  </div>
</template>

<style>

</style>
